/**
 * Public API
 * Handles public endpoints that don't require authentication
 */

import { apiClient, handleApiResponse, handleApiError } from "../config";
import type { ShopInfo, NearbyShop, Document, ApiResponse } from "../../types";

export type SenderDocState = 'uploaded' | 'printed' | 'collected' | 'expired' | 'removed';

export interface SenderOrder {
  // waiting: nothing printed yet · ready: something to pick up · completed/expired/removed: order closed
  order_status: 'waiting' | 'ready' | 'completed' | 'expired' | 'removed';
  shop_name: string;
  shop_public_id: string | null;
  shop_phone: string | null;
  documents: {
    id: number;
    original_filename: string;
    state: SenderDocState;
    print_copies: number;
    uploaded_at: string | null;
    printed_at: string | null;
  }[];
}

export class PublicAPI {
  /**
   * Get list of nearby shops
   */
  static async getShops(
    params: {
      lat?: number;
      lng?: number;
      radius?: number;
      city?: string;
    } = {}
  ): Promise<NearbyShop[]> {
    try {
      const response = await apiClient.get("/api/v1/public/shops", { params });
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Alias for getShops
   */
  static async getNearbyShops(
    params: {
      lat?: number;
      lng?: number;
      radius?: number;
      city?: string;
    } = {}
  ): Promise<{ data: { shops: NearbyShop[] } } | NearbyShop[]> {
    const shops = await PublicAPI.getShops(params);
    return { data: { shops } };
  }

  /**
   * Get shop information by QR code ID
   */
  static async getShopInfo(qrCodeId: string): Promise<ShopInfo> {
    try {
      const response = await apiClient.get(`/api/v1/public/shops/${qrCodeId}`);
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Confirm document receipt using token
   */
  static async confirmDocument(token: string): Promise<ApiResponse<string>> {
    try {
      const response = await apiClient.post(
        `/api/v1/public/documents/${token}/confirm/`
      );
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get document details for confirmation (public endpoint)
   */
  static async getDocumentForConfirmation(token: string): Promise<Document> {
    try {
      const response = await apiClient.get(`/api/v1/public/documents/${token}/`);
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get document status by ID (for sender tracking)
   */
  static async getDocumentStatus(documentId: number): Promise<Document> {
    try {
      const response = await apiClient.get(
        `/api/v1/public/documents/status/${documentId}/`
      );
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get a sender's order (all files from one upload batch) by its private token
   */
  static async getOrder(orderToken: string): Promise<SenderOrder> {
    try {
      const response = await apiClient.get(
        `/api/v1/public/orders/${encodeURIComponent(orderToken)}`
      );
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Sender confirms they collected every printed file in the order
   */
  static async confirmOrder(orderToken: string): Promise<SenderOrder> {
    try {
      const response = await apiClient.post(
        `/api/v1/public/orders/${encodeURIComponent(orderToken)}/confirm`
      );
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Confirm receipt of printed document (sender confirmation)
   */
  static async confirmReceipt(
    documentId: number
  ): Promise<ApiResponse<string>> {
    try {
      const response = await apiClient.post(
        `/api/v1/public/documents/${documentId}/confirm-receipt/`
      );
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }
}

// Export default instance for convenience
export const publicAPI = PublicAPI;
